import styled from 'styled-components';
import type { PtoRdEditsAccess, PtoVolumePosition } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  dateTimeRu,
  editBadge,
  editNote,
  engineerEdit,
  isChangeDocRow,
  isClosedRow,
  isTableRow,
  ISSUE_LABELS,
  looksUnrecognized,
} from './logic';
import type { RowEditAction } from './RowEditDialog';
import type { RdVolumeEditAccess } from './useRdVolumeEditAccess';

/** Пометка правки под наименованием: «было → стало», лист, кто и когда. */
const Note = styled.div<{ $warn: boolean }>`
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.status.warning : theme.colors.text.secondary)};
  overflow-wrap: anywhere;
`;

/** Бейдж правки — кнопка: открывает историю позиции. */
const BadgeButton = styled.button<{ $tone: 'ok' | 'warn' | 'muted' }>`
  display: inline-block;
  margin-top: 4px;
  white-space: nowrap;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  cursor: pointer;
  color: ${({ $tone, theme }) =>
    $tone === 'ok'
      ? theme.colors.status.info
      : $tone === 'warn'
        ? theme.colors.status.warning
        : theme.colors.text.secondary};
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === 'ok'
        ? theme.colors.status.info
        : $tone === 'warn'
          ? theme.colors.status.warning
          : theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

/** Пометка правки у строки (видна всем, кто видит экран): исправленное
 *  значение — с «было → стало», листом, причиной, кем (ФИО, роль) и когда;
 *  ручная строка; позиция «ДИ» — «по документу № … от … (до выдачи ревизии)». */
export function EditNote({
  position,
  access,
}: {
  position: PtoVolumePosition;
  access: PtoRdEditsAccess | null;
}) {
  const note = editNote(position, access);
  if (!note) return null;
  const status = engineerEdit(position.raw)?.status;
  const warn = status === 'conflict' || status === 'not_in_revision' || status === 'needs_decision';
  return <Note $warn={warn}>{note}</Note>;
}

/** Бейдж правки в «Статусе»: подсказка — «было → стало», кто и когда; клик —
 *  история позиции. */
export function EditBadgeButton({
  position,
  access,
  onOpen,
}: {
  position: PtoVolumePosition;
  access: PtoRdEditsAccess | null;
  onOpen: () => void;
}) {
  const badge = editBadge(position);
  if (!badge) return null;
  const e = engineerEdit(position.raw);
  const title = [editNote(position, access), e?.byName ? `${e.byName}, ${dateTimeRu(e.at)}` : null]
    .filter(Boolean)
    .join('\n');
  return (
    <div>
      <BadgeButton
        type="button"
        $tone={badge.tone}
        title={`${title}\nНажмите — история позиции`}
        onClick={onOpen}
      >
        {badge.label}
      </BadgeButton>
    </div>
  );
}

/** Кнопки правки «Инженер+» в строке: «Исправить…» у нераспознанных строк
 *  таблицы (признаки — как на сервере; окно сверяет их по `edit-options`),
 *  «Правка…» — у прочих (там же «Строка не распознана программой», «Спорно»,
 *  история). У закрытых позиций — только история. */
export function RowEditButtons({
  position: p,
  rights,
  onOpen,
}: {
  position: PtoVolumePosition;
  rights: RdVolumeEditAccess;
  onOpen: (action: RowEditAction) => void;
}) {
  if (!rights.enabled || !rights.canEdit || isClosedRow(p)) return null;
  const issues = looksUnrecognized(p);
  const live = engineerEdit(p.raw);
  if (isTableRow(p) && issues.length > 0 && !live) {
    return (
      <Button
        $variant="secondary"
        $size="sm"
        title={`Не распознано: ${issues.map((i) => ISSUE_LABELS[i]).join(', ')}`}
        onClick={() => onOpen(issues.includes('per_unit') ? 'per_unit' : 'fix')}
      >
        Исправить…
      </Button>
    );
  }
  // Действующая правка «Исправить» — сразу к её значению; прочие правки —
  // к истории (там же «Отменить» / «Отозвать»); строка без правки — к
  // «Исправить» (распознанная строка откроет «Спорно» с пояснением).
  const initial: RowEditAction = !live ? 'fix' : live.kind === 'fix' ? 'fix' : 'history';
  return (
    <Button
      $variant="ghost"
      $size="sm"
      title={
        isChangeDocRow(p)
          ? 'Позиция по документу изменения: отозвать, история'
          : 'Правка строки: исправить, разделить, внести пропущенную строку, «Спорно», история'
      }
      onClick={() => onOpen(initial)}
    >
      Правка…
    </Button>
  );
}
